import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Send, Save, Loader2 } from "lucide-react";

export default function AlertEmailSettings() {
  const { user } = useAuth();
  const [st, setSt] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState("");

  const pusat = ["logistik", "purchasing", "keuangan", "sales"].includes(user.role);

  useEffect(() => {
    if (!pusat) return;
    http.get("/settings/alert-email").then((r) => {
      setSt(r.data);
      setText((r.data.recipients || []).join(", "));
    }).catch(() => setSt(false));
  }, [pusat]);

  if (!pusat || !st) return null;

  const save = async (enabled = st.enabled) => {
    setBusy("save");
    try {
      const { data } = await http.put("/settings/alert-email", {
        recipients: text.split(",").map((s) => s.trim()).filter(Boolean),
        enabled,
      });
      setSt(data);
      setText((data.recipients || []).join(", "));
      toast.success("Pengaturan email disimpan");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      const { data } = await http.post("/alerts/email/send-now");
      toast.success(`Email ${data.status}`);
      const r = await http.get("/settings/alert-email");
      setSt(r.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="erp-panel p-5" data-testid="alert-email-settings">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-[#4338CA]" />
        <span className="mono text-[11px] uppercase tracking-widest text-[#525252]">
          Notifikasi Email Harian — 08:00 WIB
        </span>
      </div>

      <div className="mt-4 flex flex-col lg:flex-row gap-3 lg:items-end">
        <div className="flex-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-[#525252]">
            Email Penerima (pisahkan dengan koma, maks 10)
          </label>
          <input
            data-testid="alert-email-recipients-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="direksi@perusahaan.co.id, manager@perusahaan.co.id"
            className="mt-1 w-full h-10 px-3 border border-neutral-300 bg-white outline-none focus:ring-2 focus:ring-[#4338CA] focus:border-transparent text-sm"
          />
        </div>

        <label className="flex items-center gap-2 text-sm h-10" data-testid="alert-email-enabled-wrap">
          <input
            data-testid="alert-email-enabled-checkbox"
            type="checkbox"
            checked={st.enabled}
            onChange={(e) => {
              setSt({ ...st, enabled: e.target.checked });
              save(e.target.checked);
            }}
            className="h-4 w-4 accent-[#4338CA]"
          />
          Aktif
        </label>

        <button
          data-testid="alert-email-save-button"
          onClick={() => save()}
          disabled={!!busy}
          className="h-10 px-4 border border-neutral-800 text-xs uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-60"
        >
          {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Simpan
        </button>

        <button
          data-testid="alert-email-send-now-button"
          onClick={sendNow}
          disabled={!!busy}
          className="h-10 px-4 bg-[#4338CA] text-white text-xs uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-[#3730A3] transition-colors disabled:opacity-60"
        >
          {busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Kirim Sekarang
        </button>
      </div>

      <div className="mt-3 text-xs text-[#525252]" data-testid="alert-email-status">
        {st.last_sent
          ? `Pengiriman terakhir: ${st.last_sent.slice(0, 16).replace("T", " ")} UTC — ${st.last_status || "-"}`
          : "Belum ada riwayat pengiriman."}
      </div>
    </div>
  );
}
